import React, { useState } from 'react';
import { Globe, Smartphone, Cloud, Bot, Sparkles, Server } from 'lucide-react';

const services = [
  {
    icon: <Globe size={28} />,
    title: 'Web Development',
    badge: 'Frontend & Backend',
    desc: 'High-performance web applications with React, Next.js, Node.js, and enterprise-grade MERN stacks that scale to millions of users.',
    tags: ['React', 'Next.js', 'Node.js', 'MERN', 'Enterprise Apps', 'TypeScript'],
    color: '#258ACB',
  },
  {
    icon: <Smartphone size={28} />,
    title: 'Mobile App Development',
    badge: 'iOS & Android',
    desc: 'Cross-platform mobile experiences using Flutter and React Native. Ship once, run everywhere — without sacrificing native performance.',
    tags: ['Flutter', 'React Native', 'iOS', 'Android', 'Cross-Platform'],
    color: '#3BA3E8',
  },
  {
    icon: <Cloud size={28} />,
    title: 'DevOps & Cloud',
    badge: 'Infrastructure',
    desc: 'End-to-end cloud architecture, CI/CD automation, containerization, and managed infrastructure on AWS, Azure, and GCP.',
    tags: ['AWS', 'Azure', 'Docker', 'Kubernetes', 'CI/CD', 'Terraform'],
    color: '#1A6FA8',
  },
  {
    icon: <Bot size={28} />,
    title: 'Agentic AI',
    badge: 'Autonomous Systems',
    desc: 'Autonomous AI workflows that think, plan, and act. We build multi-agent systems and AI orchestration pipelines for complex business automation.',
    tags: ['AI Agents', 'LangChain', 'Multi-Agent', 'AutoGPT', 'Workflows'],
    color: '#258ACB',
  },
  {
    icon: <Sparkles size={28} />,
    title: 'Generative AI',
    badge: 'LLM & RAG',
    desc: 'LLM integration, RAG pipelines, custom chatbots, and AI-powered automation that transforms how your team works and your customers engage.',
    tags: ['OpenAI', 'RAG', 'LLMs', 'Chatbots', 'Fine-tuning', 'Vector DBs'],
    color: '#3BA3E8',
  },
  {
    icon: <Server size={28} />,
    title: 'Cloud Architecture',
    badge: 'Strategy & Design',
    desc: 'Cloud strategy, migration planning, cost optimization, and scalability reviews for teams moving to or maturing in the cloud.',
    tags: ['Cloud Migration', 'FinOps', 'Architecture', 'Microservices'],
    color: '#1A6FA8',
  },
];

export default function Services() {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="services" style={{ padding: '7rem 0', position: 'relative', overflow: 'hidden' }}>
      <div className="glow-orb" style={{
        width: '600px', height: '600px',
        background: 'radial-gradient(circle, rgba(37,138,203,0.07) 0%, transparent 70%)',
        top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      }} />

      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label" style={{ margin: '0 auto 1.5rem' }}>
            <span>◆</span> What We Do
          </div>
          <h2 className="section-heading" style={{ margin: '0 auto 1rem' }}>
            Services built for <span className="gradient-text">modern scale</span>
          </h2>
          <p className="section-subtext" style={{ margin: '0 auto', textAlign: 'center' }}>
            From pixels to petabytes — we cover every layer of your digital stack with precision engineering and AI-first thinking.
          </p>
        </div>

        {/* Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '1.5rem',
        }}>
          {services.map((svc, i) => (
            <div
              key={svc.title}
              className="glass-card"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'default' }}
            >
              {/* Icon */}
              <div style={{
                width: '56px', height: '56px', borderRadius: '14px',
                background: `rgba(37,138,203,0.12)`,
                border: `1px solid rgba(37,138,203,${hovered === i ? 0.5 : 0.2})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: svc.color, marginBottom: '1.25rem',
                transition: 'all 0.3s',
                boxShadow: hovered === i ? `0 0 20px rgba(37,138,203,0.25)` : 'none',
              }}>
                {svc.icon}
              </div>

              {/* Badge */}
              <div style={{
                display: 'inline-block', fontSize: '0.72rem', fontWeight: 600,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--accent)', background: 'rgba(37,138,203,0.08)',
                border: '1px solid rgba(37,138,203,0.2)', borderRadius: '4px',
                padding: '0.2rem 0.6rem', marginBottom: '0.75rem',
              }}>{svc.badge}</div>

              <h3 style={{
                fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 700,
                marginBottom: '0.75rem',
              }}>{svc.title}</h3>

              <p style={{
                fontSize: '0.92rem', color: 'var(--text-secondary)',
                lineHeight: 1.7, marginBottom: '1.25rem',
              }}>{svc.desc}</p>

              {/* Tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {svc.tags.map(tag => (
                  <span key={tag} style={{
                    fontSize: '0.75rem', fontWeight: 500,
                    color: 'var(--text-muted)', background: 'rgba(37,138,203,0.05)',
                    border: '1px solid var(--border)', borderRadius: '4px',
                    padding: '0.2rem 0.6rem',
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
